import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { CheckCircle, Loader2 } from "lucide-react";
import { API_URL } from "../config/api";

const SERVICES = ["Ocean Freight", "Air Freight", "Road Transport", "Rail Freight", "Customs Clearance", "Warehousing", "Customer Support", "Other"];
const ASPECTS = ["Delivery reliability", "Transit time", "Communication", "Price", "Documentation accuracy", "Customs handling", "Shipment visibility / tracking", "Responsiveness", "Problem resolution", "Professionalism of staff", "Digital tools / portal"];

export default function NPSSurveyPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [survey, setSurvey] = useState(null);
  const [score, setScore] = useState(params.get("score") !== null ? parseInt(params.get("score")) : null);
  const [reason, setReason] = useState("");
  const [service, setService] = useState("");
  const [aspects, setAspects] = useState([]);
  const [improvement, setImprovement] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/nps/survey/${token}`);
        setSurvey(r.data);
        if (r.data.status === "completed") setDone(true);
      } catch {
        toast.error("Survey not found or expired");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const toggleAspect = (a) => setAspects((prev) => prev.includes(a) ? prev.filter((x) => x !== a) : (prev.length >= 3 ? prev : [...prev, a]));

  const submit = async () => {
    if (score === null) { toast.error("Please select a score from 0 to 10"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/nps/submit/${token}`, { score, reason, service, aspects, improvement });
      setDone(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;

  if (done) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full"><CardContent className="p-10 text-center">
        <CheckCircle className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-slate-800 mb-2">Thank you!</h1>
        <p className="text-slate-500">Your feedback helps us improve our logistics services.</p>
      </CardContent></Card>
    </div>
  );

  const q = survey?.survey_type === "relationship"
    ? "Thinking about your overall experience with Europacific over the past 6 months, how likely are you to recommend us to a colleague or business partner?"
    : `How likely are you to recommend Europacific to a colleague or business partner${survey?.pf_number ? ` for shipment ${survey.pf_number}` : ""}?`;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="nps-survey-page">
      <div className="max-w-2xl mx-auto">
        <div className="bg-gradient-to-r from-[#003366] to-[#004488] text-white rounded-t-xl p-7 text-center">
          <h1 className="text-2xl font-bold">Help us improve our logistics services</h1>
          <p className="opacity-90 mt-1">Europacific Logistics · takes less than 2 minutes</p>
        </div>
        <Card className="rounded-t-none"><CardContent className="p-6 space-y-7">
          <div>
            <p className="font-semibold text-slate-800 mb-3">{q} <span className="text-red-500">*</span></p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {Array.from({ length: 11 }, (_, n) => {
                const c = n <= 6 ? "detractor" : n <= 8 ? "passive" : "promoter";
                const active = score === n;
                const base = c === "detractor" ? "bg-red-500" : c === "passive" ? "bg-amber-500" : "bg-emerald-500";
                return (
                  <button key={n} data-testid={`nps-score-${n}`} onClick={() => setScore(n)}
                    className={`w-11 h-11 rounded-lg font-bold text-white transition-transform ${base} ${active ? "ring-4 ring-offset-1 ring-slate-800 scale-110" : "opacity-80 hover:opacity-100"}`}>
                    {n}
                  </button>
                );
              })}
            </div>
            <div className="flex justify-between text-xs text-slate-500 mt-2 px-1"><span>0 = Not at all likely</span><span>10 = Extremely likely</span></div>
          </div>

          <div>
            <p className="text-sm font-medium text-slate-700 mb-1.5">Why did you give this score?</p>
            <Textarea data-testid="nps-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Tell us the main reason for your rating" rows={3} />
          </div>

          <div>
            <p className="text-sm font-medium text-slate-700 mb-1.5">Which service did your feedback relate to?</p>
            <div className="flex flex-wrap gap-2">
              {SERVICES.map((s) => (
                <button key={s} data-testid={`nps-service-${s}`} onClick={() => setService(s)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${service === s ? "bg-[#003366] text-white border-[#003366]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>{s}</button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-slate-700 mb-1.5">Which aspects were most important to you? <span className="text-slate-400">(up to 3)</span></p>
            <div className="flex flex-wrap gap-2">
              {ASPECTS.map((a) => (
                <button key={a} onClick={() => toggleAspect(a)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${aspects.includes(a) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>{a}</button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-slate-700 mb-1.5">What could we improve?</p>
            <Textarea data-testid="nps-improvement" value={improvement} onChange={(e) => setImprovement(e.target.value)} placeholder="Your suggestions" rows={3} />
          </div>

          <Button data-testid="nps-submit" onClick={submit} disabled={submitting} className="w-full bg-[#003366] hover:bg-[#004488] h-11 text-base">
            {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit feedback"}
          </Button>
        </CardContent></Card>
      </div>
    </div>
  );
}
